import { AICourse } from './types';
import { 
  MODULE_1_AI_INTRODUCTION,
  AI_FUNDAMENTALS_MODULE_2,
  AI_FUNDAMENTALS_MODULE_3,
  AI_FUNDAMENTALS_MODULE_4
} from './modules/ai-fundamentals';
import { 
  FINANCIAL_AI_MODULE_1_FOUNDATIONS,
  FINANCIAL_AI_MODULE_2_ALGORITHMIC_TRADING,
  FINANCIAL_AI_MODULE_3_RISK_MANAGEMENT
} from './modules/financial-ai';
import { 
  HEALTHCARE_AI_MODULE_1_FOUNDATIONS,
  HEALTHCARE_AI_MODULE_2_MEDICAL_IMAGING,
  HEALTHCARE_AI_MODULE_3
} from './modules/healthcare-ai';
import { 
  GAMING_AI_MODULE_1,
  GAMING_AI_MODULE_2,
  GAMING_AI_MODULE_3,
  GAMING_AI_MODULE_4,
  GAMING_AI_MODULE_5,
  GAMING_AI_MODULE_6
} from './modules/gaming-ai';
import { 
  AUTONOMOUS_SYSTEMS_MODULE_1,
  AUTONOMOUS_SYSTEMS_MODULE_2,
  AUTONOMOUS_SYSTEMS_MODULE_3
} from './modules/autonomous-systems';
import { 
  MLOPS_MODULE_1,
  MLOPS_MODULE_2
} from './modules/mlops';

import { RAG_SUPABASE_MODULE_1 } from './modules/rag-supabase';

import {
  STREAMLIT_AI_MODULE,
  GRADIO_ML_MODULE,
  HUGGINGFACE_INTEGRATION_MODULE,
  NOCODE_AI_TOOLS_MODULE,
  AI_API_INTEGRATION_MODULE,
  VOICE_AI_DEVELOPMENT_MODULE
} from './modules/ai-prototyping';

import {
  CUSTOM_LLM_INTEGRATION_MODULE,
  FUNCTION_CALLING_TOOLS_MODULE,
  ADVANCED_PROMPT_ENGINEERING_MODULE,
  LLM_EVALUATION_MODULE,
  ENTERPRISE_LLM_DEPLOYMENT_MODULE
} from './modules/advanced-llm-integration';

// Massive content imports removed to fix page freezes:
// - ai-agent-development (748 lines)
// - enhanced-fundamentals (1,071 lines)
// - computer-vision-ai (1,043 lines)
// - reinforcement-learning-fundamentals
// - multi-agent-rl
// - robotics-rl
// - advanced-rl-algorithms
// - ai-ethics-fundamentals folder
// - ai-bias-fairness folder

import { EDGE_AI_OPTIMIZATION_MODULES } from './modules/edge-ai-optimization';
import { SMART_IOT_SYSTEMS_MODULES } from './modules/smart-iot-systems';

// Data Pipeline & MLOps - will use new optimized modules
import { DATA_PIPELINE_MODULES } from './modules/data-pipeline';
import {
  QUANTUM_ML_FUNDAMENTALS_MODULE,
  QUANTUM_ALGORITHMS_MODULE
} from './modules/quantum-ml-fundamentals';
import {
  BLOCKCHAIN_AI_INTEGRATION_MODULE,
  DECENTRALIZED_AI_GOVERNANCE_MODULE
} from './modules/blockchain-ai-integration';

export const AI_COURSES: AICourse[] = [
  // ai-fundamentals course removed - used enhanced-fundamentals.ts (1,071 lines)
  {
    id: 'financial-ai',
    title: 'Financial AI & FinTech',
    description: 'Apply artificial intelligence and machine learning techniques to financial services, trading, and risk management.',
    duration: '25 hours',
    level: 'advanced',
    estimatedHours: 25,
    category: 'Domain Expertise',
    tags: ['AI', 'Finance', 'Trading', 'Risk Management', 'FinTech'],
    prerequisites: ['Basic ML knowledge', 'Financial markets understanding', 'Python programming'],
    learningOutcomes: [
      'Build algorithmic trading systems',
      'Implement risk management models',
      'Create fraud detection systems',
      'Develop credit scoring algorithms',
      'Apply AI to financial data analysis'
    ],
    modules: [
      FINANCIAL_AI_MODULE_1_FOUNDATIONS,
      FINANCIAL_AI_MODULE_2_ALGORITHMIC_TRADING,
      FINANCIAL_AI_MODULE_3_RISK_MANAGEMENT
    ]
  },
  {
    id: 'healthcare-ai',
    title: 'Healthcare AI & Medical Intelligence',
    description: 'Explore AI applications in healthcare, medical imaging, clinical decision support, and personalized medicine.',
    duration: '30 hours',
    level: 'advanced',
    estimatedHours: 30,
    category: 'Domain Expertise',
    tags: ['AI', 'Healthcare', 'Medical', 'Imaging', 'Clinical'],
    prerequisites: ['ML fundamentals', 'Basic medical knowledge', 'Python/R programming'],
    learningOutcomes: [
      'Analyze medical imaging with deep learning',
      'Build clinical decision support systems',
      'Implement drug discovery algorithms',
      'Create personalized medicine models',
      'Understand healthcare AI ethics and regulations'
    ],
    modules: [
      HEALTHCARE_AI_MODULE_1_FOUNDATIONS,
      HEALTHCARE_AI_MODULE_2_MEDICAL_IMAGING,
      HEALTHCARE_AI_MODULE_3
    ]
  },
  {
    id: 'gaming-ai',
    title: 'Gaming AI & Interactive Entertainment',
    description: 'Learn to create intelligent game characters, adaptive gameplay systems, and AI-driven game mechanics.',
    duration: '20 hours',
    level: 'intermediate',
    estimatedHours: 20,
    category: 'Domain Expertise',
    tags: ['AI', 'Gaming', 'NPCs', 'Procedural Generation', 'Game Development'],
    prerequisites: ['Programming experience', 'Basic AI concepts', 'Game development basics'],
    learningOutcomes: [
      'Implement intelligent NPC behavior',
      'Create adaptive difficulty systems',
      'Build procedural content generation',
      'Develop player analytics systems',
      'Apply reinforcement learning to games'
    ],
    modules: [
      GAMING_AI_MODULE_1,
      GAMING_AI_MODULE_2,
      GAMING_AI_MODULE_3,
      GAMING_AI_MODULE_4,
      GAMING_AI_MODULE_5,
      GAMING_AI_MODULE_6
    ]
  },
  {
    id: 'autonomous-systems',
    title: 'Autonomous Systems & Robotics AI',
    description: 'Build intelligent autonomous systems including self-driving vehicles, drones, and robotic systems.',
    duration: '35 hours',
    level: 'advanced',
    estimatedHours: 35,
    category: 'Domain Expertise',
    tags: ['AI', 'Robotics', 'Autonomous Vehicles', 'Computer Vision', 'Control Systems'],
    prerequisites: ['Strong programming skills', 'ML/DL knowledge', 'Control systems basics'],
    learningOutcomes: [
      'Design autonomous vehicle systems',
      'Implement robot perception and navigation',
      'Build intelligent control systems',
      'Create multi-agent coordination',
      'Apply safety and reliability principles'
    ],
    modules: [
      AUTONOMOUS_SYSTEMS_MODULE_1,
      AUTONOMOUS_SYSTEMS_MODULE_2,
      AUTONOMOUS_SYSTEMS_MODULE_3
    ]
  },
  {
    id: 'mlops-deployment',
    title: 'MLOps & Production AI Systems',
    description: 'Learn to deploy, monitor, and maintain machine learning systems in production environments.',
    duration: '15 hours',
    level: 'intermediate',
    estimatedHours: 15,
    category: 'MLOps',
    tags: ['MLOps', 'Deployment', 'Production', 'Monitoring', 'CI/CD'],
    prerequisites: ['ML experience', 'Software engineering basics', 'Cloud computing familiarity'],
    learningOutcomes: [
      'Deploy ML models to production',
      'Implement CI/CD for ML systems',
      'Monitor model performance and drift',
      'Scale ML systems efficiently',
      'Ensure ML system reliability and security'
    ],
    modules: [
      MLOPS_MODULE_1,
      MLOPS_MODULE_2
    ]
  },
  {
    id: 'genai-rag-supabase',
    title: 'Generative AI with RAG on Supabase',
    description: 'Build end-to-end RAG applications using Supabase for storage, vectors, and APIs with production best practices.',
    duration: '12 hours',
    level: 'intermediate',
    estimatedHours: 12,
    category: 'Generative AI',
    tags: ['AI', 'RAG', 'Supabase', 'Embeddings'],
    prerequisites: ['Basic ML knowledge', 'TypeScript/Node basics'],
    learningOutcomes: [
      'Implement embeddings and vector search on Supabase',
      'Build retrieval pipelines and prompt construction',
      'Ship a production-ready RAG API',
    ],
    modules: [
      RAG_SUPABASE_MODULE_1
    ]
  },
  // ai-agent-development course removed - 748 lines causing page freezes
  {
    id: 'ai-prototyping',
    title: 'AI Prototyping & Rapid Development',
    description: 'Rapidly prototype and deploy AI applications using Streamlit, Gradio, Hugging Face, and no-code platforms. Perfect for quickly validating AI concepts and building demos.',
    level: 'intermediate',
    duration: '18-22 hours',
    estimatedHours: 20,
    modules: [
      STREAMLIT_AI_MODULE,
      GRADIO_ML_MODULE,
      HUGGINGFACE_INTEGRATION_MODULE,
      NOCODE_AI_TOOLS_MODULE,
      AI_API_INTEGRATION_MODULE,
      VOICE_AI_DEVELOPMENT_MODULE
    ],
    tags: ['Streamlit', 'Gradio', 'Hugging Face', 'No-Code AI', 'Rapid Prototyping', 'Voice AI'],
    prerequisites: ['Python basics', 'Basic ML concepts'],
    learningOutcomes: [
      'Build interactive AI apps with Streamlit',
      'Create stunning ML demos with Gradio',
      'Deploy models using Hugging Face ecosystem',
      'Leverage no-code platforms for AI development',
      'Integrate multiple AI APIs and services',
      'Develop voice-enabled AI applications',
      'Rapidly validate and iterate on AI concepts'
    ],
    category: 'AI Prototyping'
  },
  {
    id: 'advanced-llm-integration',
    title: 'Advanced LLM Integration & Applications',
    description: 'Master enterprise-grade LLM integration with OpenAI API, custom models, function calling, advanced prompting, and production deployment strategies.',
    level: 'advanced',
    duration: '20-25 hours',
    estimatedHours: 22,
    modules: [
      CUSTOM_LLM_INTEGRATION_MODULE,
      FUNCTION_CALLING_TOOLS_MODULE,
      ADVANCED_PROMPT_ENGINEERING_MODULE,
      LLM_EVALUATION_MODULE,
      ENTERPRISE_LLM_DEPLOYMENT_MODULE
    ],
    tags: ['OpenAI API', 'Custom LLMs', 'Function Calling', 'Prompt Engineering', 'Enterprise AI', 'Model Evaluation'],
    prerequisites: ['Python proficiency', 'API development experience', 'Cloud platforms knowledge'],
    learningOutcomes: [
      'Master OpenAI API and fine-tuning workflows',
      'Integrate custom and open-source LLMs',
      'Implement sophisticated function calling systems',
      'Design advanced prompting strategies',
      'Build comprehensive evaluation frameworks',
      'Deploy LLMs securely in enterprise environments',
      'Optimize cost and performance at scale'
    ],
    category: 'Enterprise LLM'
  },
  // computer-vision-ai course removed - 1,043 lines causing page freezes
  // reinforcement-learning-fundamentals course removed
  // multi-agent-rl course removed
  // robotics-rl-applications course removed
  {
    id: 'smart-iot-systems',
    title: 'Smart IoT Systems Development',
    description: 'Create intelligent IoT ecosystems with sensor fusion, federated learning, and distributed AI.',
    level: 'advanced',
    duration: '16-20 hours',
    estimatedHours: 18,
    modules: SMART_IOT_SYSTEMS_MODULES,
    tags: ['Smart IoT', 'Sensor Fusion', 'Federated Learning', 'Distributed AI', 'Smart Cities'],
    prerequisites: ['IoT fundamentals', 'Distributed systems', 'AI/ML experience'],
    learningOutcomes: [
      'Build smart IoT systems with AI',
      'Implement sensor fusion and federated learning',
      'Design scalable distributed AI architectures'
    ],
    category: 'Edge AI'
  },
  // ai-ethics-fundamentals course removed - causing page freezes
  // reinforcement-learning-robotics course removed
  {
    id: 'data-pipeline-mlops',
    title: 'Data Pipeline & MLOps',
    description: 'Build scalable data pipelines and implement MLOps practices for production ML systems.',
    level: 'intermediate',
    duration: '8-10 hours',
    estimatedHours: 9,
    modules: DATA_PIPELINE_MODULES,
    tags: ['mlops', 'data-engineering', 'deployment', 'monitoring'],
    prerequisites: ['Machine Learning Fundamentals', 'Python Programming'],
    learningOutcomes: [
      'Design scalable data pipelines',
      'Implement CI/CD for ML models',
      'Set up model monitoring and observability',
      'Deploy ML models in production'
    ],
    category: 'MLOps & Engineering'
  },
  {
    id: 'quantum-machine-learning',
    title: 'Quantum Machine Learning',
    description: 'Explore quantum computing applications in machine learning.',
    level: 'advanced',
    duration: '8-10 hours',
    estimatedHours: 9,
    modules: [QUANTUM_ML_FUNDAMENTALS_MODULE, QUANTUM_ALGORITHMS_MODULE],
    tags: ['quantum-computing', 'qml', 'algorithms', 'optimization'],
    prerequisites: ['Machine Learning Fundamentals', 'Linear Algebra'],
    learningOutcomes: [
      'Understand quantum computing principles',
      'Implement quantum machine learning algorithms',
      'Build quantum neural networks',
      'Apply quantum optimization techniques'
    ],
    category: 'Advanced Algorithms'
  },
  {
    id: 'blockchain-ai-integration',
    title: 'Blockchain & AI Integration',
    description: 'Integrate blockchain technology with AI systems and create decentralized AI.',
    level: 'intermediate',
    duration: '8-10 hours',
    estimatedHours: 9,
    modules: [BLOCKCHAIN_AI_INTEGRATION_MODULE, DECENTRALIZED_AI_GOVERNANCE_MODULE],
    tags: ['blockchain', 'dao', 'decentralized-ai', 'smart-contracts'],
    prerequisites: ['Machine Learning Fundamentals', 'Blockchain Basics'],
    learningOutcomes: [
      'Build decentralized AI systems',
      'Implement smart contracts for ML governance',
      'Create tokenized AI models',
      'Design DAO governance for AI'
    ],
    category: 'Blockchain & Web3'
  }
];

// Development-only validation
if (typeof window !== 'undefined' && process.env.NODE_ENV === 'development') {
  console.log(`🎓 AI Course System Loaded: ${AI_COURSES.length} courses across ${new Set(AI_COURSES.map(c => c.category)).size} categories`);
}
